/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import PDFDocument from "@react-pdf/pdfkit";
import { isEmpty } from "lodash";
import { v4 as uuid } from "uuid";

import { convertImageUrlToBase64 } from "./convertImageUrlToBase64";

export interface CreateIndependentContractorBadgeParams {
  details: {
    firstName: string;
    lastName: string;
    qualification: string;
    licenseNumber?: string | undefined;
    profilePictureBase64: string;
  };
  logoUrl?: string;
  sizeOptions?: {
    /**
     * Page size for the PDF output - "A4" or [width, height]
     */
    size?: "A4" | [number, number];
    /**
     * Margin around the badge in millimeters. Useful when outputting to standard page sizes
     * (like A4) to provide spacing since the badge will be positioned at the top-left.
     * Allows room for cutting the badge out of paper without damaging borders.
     * @default 0
     */
    margin?: number;
  };
}

/**
 * Convert a distance in millimeters to points (to be used with PDFKit)
 * @param mm - distance in millimeters
 * @returns
 */
const mmToPoints = (mm: number) => mm * 2.834_645_669;

/**
 * Shortens name if too long by converting last name to initial
 */
function getBadgeDisplayName(firstName: string, lastName: string, maxLength = 30): string {
  const fullName = `${firstName} ${lastName}`;

  if (fullName.length <= maxLength) {
    return fullName;
  }

  return `${firstName} ${lastName.charAt(0)}.`;
}

const heightInMm = 53.98;
const widthInMm = 85.6;

export const aspectRatio = widthInMm / heightInMm;

const pictureWidthInMm = 24;
const pictureHeightInMm = 30;
export const pictureAspectRatio = pictureWidthInMm / pictureHeightInMm;

interface GetOptimalFontSettingsParams {
  pdf: typeof PDFDocument;
  text: string;
  maxWidth: number;
  maxHeight: number;
  font: string;
  minFontSize: number;
  maxFontSize: number;
  lineGap?: number;
}

/**
 * Calculates optimal font size for text to fit within specified dimensions
 */
function getOptimalFontSettings({
  pdf,
  text,
  maxWidth,
  maxHeight,
  font,
  minFontSize,
  maxFontSize,
  lineGap = 0,
}: GetOptimalFontSettingsParams) {
  let bestFontSize = minFontSize;

  // Binary search for optimal font size
  let low = minFontSize;
  let high = maxFontSize;

  while (low <= high) {
    const fontSize = Math.floor((low + high) / 2);
    pdf.font(font).fontSize(fontSize);

    const width = pdf.widthOfString(text);
    const height = pdf.heightOfString(text, { width: maxWidth, lineGap });

    if (width <= maxWidth && height <= maxHeight) {
      bestFontSize = fontSize;
      low = fontSize + 1; // Try larger size
    } else {
      high = fontSize - 1; // Try smaller size
    }
  }

  // Recalculate final measurements with the best font size
  const actualHeight = pdf
    .font(font)
    .fontSize(bestFontSize)
    .heightOfString(text, { width: maxWidth, lineGap });

  return {
    fontSize: bestFontSize,
    actualHeight,
  };
}

const basePath = process.env.REACT_APP_BASE_PATH ?? "/";
const defaultLogoUrl = `${basePath}assets/images/badge-bunny.jpg`; // Present in our public folder

const defaultProfilePictureUrl = `${basePath}assets/images/avatar.jpg`;

export async function createIndependentContractorBadgePdf({
  details,
  logoUrl = defaultLogoUrl,
  sizeOptions,
}: CreateIndependentContractorBadgeParams): Promise<{
  blob: Blob;
  pdfKey: string;
}> {
  const text = {
    fonts: {
      regular: "Helvetica",
      bold: "Helvetica-Bold",
    },
  };

  const palette = {
    primary: {
      foreground: "#0C1C43",
      background: "#FFFFFF",
    },
  };

  const badgeMargin = isDefined(sizeOptions?.margin)
    ? mmToPoints(sizeOptions.margin)
    : mmToPoints(0);
  const badgeBorderWidth = mmToPoints(0.5);

  const badge = {
    // PDFKit renders borders centered on rectangle edges (half inside, half outside)
    // Subtract border width so total size including border equals credit card dimensions
    width: mmToPoints(widthInMm) - badgeBorderWidth, // Credit card size minus border (border is centered)
    height: mmToPoints(heightInMm) - badgeBorderWidth,
    padding: {
      top: mmToPoints(4),
      right: mmToPoints(4),
      bottom: mmToPoints(4),
      left: mmToPoints(4),
    },
    border: {
      width: mmToPoints(0.5),
      radius: mmToPoints(3.5),
    },
    margin: {
      top: badgeMargin,
      right: badgeMargin,
      bottom: badgeMargin,
      left: badgeMargin,
    },
    // Will be calculated
    x: 0,
    y: 0,
  };

  // Badge position and size within the document
  badge.x = badge.margin.left + badge.border.width / 2;
  badge.y = badge.margin.top + badge.border.width / 2;

  // Use same document size as badge size if not specified
  // Total size = credit card size + margins (border already accounted for in rectangle sizing)
  const defaultSize = [
    mmToPoints(widthInMm) + badge.margin.left + badge.margin.right,
    mmToPoints(heightInMm) + badge.margin.top + badge.margin.bottom,
  ]; // [width, height]

  const pdf = new PDFDocument({
    size: sizeOptions?.size ?? defaultSize,
    margins: { top: 0, right: 0, bottom: 0, left: 0 }, // Custom margin control
  });

  // --- Badge background with rounded corners
  pdf
    .lineWidth(badge.border.width)
    .roundedRect(badge.x, badge.y, badge.width, badge.height, badge.border.radius)
    .fillAndStroke("#FFFFFF", palette.primary.foreground);

  const defaultProfilePictureBase64 = await convertImageUrlToBase64(defaultProfilePictureUrl);

  // --- Profile picture
  const profileImage = {
    x: badge.x + badge.padding.left,
    y: badge.y + badge.padding.top,
    width: mmToPoints(pictureWidthInMm),
    height: mmToPoints(pictureHeightInMm),
    radius: mmToPoints(1.5),
    margin: {
      right: mmToPoints(3),
    },
  };

  // Save graphics state and create clipping mask for rounded image
  pdf
    .save()
    .lineWidth(0)
    .roundedRect(
      profileImage.x,
      profileImage.y,
      profileImage.width,
      profileImage.height,
      profileImage.radius
    )
    .clip()
    .image(
      isEmpty(details.profilePictureBase64)
        ? defaultProfilePictureBase64
        : details.profilePictureBase64,
      profileImage.x,
      profileImage.y,
      {
        width: profileImage.width,
        height: profileImage.height,
        cover: [profileImage.width, profileImage.height],
        align: "center",
        valign: "center",
      }
    )
    .restore();

  // --- Name
  const displayName = getBadgeDisplayName(details.firstName, details.lastName);

  const nameText = {
    x: profileImage.x + profileImage.width + profileImage.margin.right,
    y: profileImage.y + mmToPoints(1),
    size: {
      min: mmToPoints(4.9),
      max: mmToPoints(7.1),
    },
    lineGap: mmToPoints(-0.7),
    font: text.fonts.bold,
  };

  // Calculate available width and height for name text
  const nameAvailableWidth = badge.x + badge.width - nameText.x - badge.padding.right;
  const nameAvailableHeight = mmToPoints(15);

  const nameOptimal = getOptimalFontSettings({
    pdf,
    text: displayName,
    maxWidth: nameAvailableWidth,
    maxHeight: nameAvailableHeight,
    font: nameText.font,
    minFontSize: nameText.size.min,
    maxFontSize: nameText.size.max,
    lineGap: nameText.lineGap,
  });

  // Add name text to the right of the image
  pdf
    .font(nameText.font)
    .fontSize(nameOptimal.fontSize)
    .fillColor(palette.primary.foreground)
    .text(displayName, nameText.x, nameText.y, {
      align: "left",
      width: nameAvailableWidth, // Force text wrapping within available width
      lineGap: nameText.lineGap,
    });

  // --- Qualification
  const qualificationText = {
    background: {
      x: nameText.x,
      y: nameText.y + nameOptimal.actualHeight + mmToPoints(1),
      radius: mmToPoints(8),
      padding: {
        horizontal: mmToPoints(3),
        vertical: mmToPoints(1.5),
      },
      // Will be calculated
      width: 0,
      height: 0,
    },
    size: {
      min: mmToPoints(2.9),
      max: mmToPoints(3.9),
    },
    lineGap: mmToPoints(-0.3),
    font: text.fonts.regular,
  };

  // Calculate available width and height for qualification text
  const qualificationAvailableWidth = badge.x + badge.width - nameText.x - badge.padding.right;
  const qualificationAvailableHeight = mmToPoints(10);
  const qualificationMaxTextWidth =
    qualificationAvailableWidth - qualificationText.background.padding.horizontal * 2;

  const qualificationOptimal = getOptimalFontSettings({
    pdf,
    text: details.qualification,
    maxWidth: qualificationMaxTextWidth,
    maxHeight: qualificationAvailableHeight,
    font: qualificationText.font,
    minFontSize: qualificationText.size.min,
    maxFontSize: qualificationText.size.max,
    lineGap: qualificationText.lineGap,
  });

  // Calculate background dimensions with optimal font size
  pdf.font(qualificationText.font).fontSize(qualificationOptimal.fontSize);
  const qualificationWidth = pdf.widthOfString(details.qualification);

  qualificationText.background.width = Math.min(
    qualificationWidth + qualificationText.background.padding.horizontal * 2,
    qualificationAvailableWidth
  );
  qualificationText.background.height =
    qualificationOptimal.actualHeight + qualificationText.background.padding.vertical * 2;

  // Add qualification background
  pdf
    .lineWidth(0)
    .roundedRect(
      qualificationText.background.x,
      qualificationText.background.y,
      qualificationText.background.width,
      qualificationText.background.height,
      qualificationText.background.radius
    )
    .fillAndStroke(palette.primary.foreground, palette.primary.foreground);

  // Add qualification text
  const qualificationTextX =
    qualificationText.background.x + qualificationText.background.padding.horizontal;

  const qualificationTextY =
    qualificationText.background.y +
    (qualificationText.background.height - qualificationOptimal.actualHeight) / 2 +
    qualificationOptimal.fontSize * 0.16; // Correction for baseline alignment

  pdf
    .font(qualificationText.font)
    .fontSize(qualificationOptimal.fontSize)
    .fillColor(palette.primary.background)
    .text(details.qualification, qualificationTextX, qualificationTextY, {
      align: "left",
      width: qualificationMaxTextWidth,
      lineGap: qualificationText.lineGap,
    });

  // --- License number
  const licenseNumberText = {
    x: qualificationText.background.x,
    y: qualificationText.background.y + qualificationText.background.height + mmToPoints(2),
    size: {
      min: mmToPoints(3.5),
      max: mmToPoints(6),
    },
    font: text.fonts.bold,
    lineGap: mmToPoints(-0.3),
    maxWidth: badge.x + badge.width - qualificationText.background.x - badge.padding.right,
  };

  if (isDefined(details.licenseNumber) && details.licenseNumber.length > 0) {
    const licenseAvailableHeight = mmToPoints(8);

    const licenseOptimal = getOptimalFontSettings({
      pdf,
      text: details.licenseNumber,
      maxWidth: licenseNumberText.maxWidth,
      maxHeight: licenseAvailableHeight,
      font: licenseNumberText.font,
      minFontSize: licenseNumberText.size.min,
      maxFontSize: licenseNumberText.size.max,
      lineGap: licenseNumberText.lineGap,
    });

    pdf
      .font(licenseNumberText.font)
      .fontSize(licenseOptimal.fontSize)
      .fillColor(palette.primary.foreground)
      .text(details.licenseNumber, licenseNumberText.x, licenseNumberText.y, {
        align: "left",
        baseline: "top",
        width: licenseNumberText.maxWidth,
        lineGap: licenseNumberText.lineGap,
      });
  }

  // --- Through Clipboard text
  const throughClipboardTextMarginTop = mmToPoints(3.4);
  const throughClipboardText = {
    x: profileImage.x,
    y: profileImage.y + profileImage.height + throughClipboardTextMarginTop,
    font: text.fonts.regular,
    fontSize: mmToPoints(3.9),
  };

  pdf
    .font(throughClipboardText.font)
    .fontSize(throughClipboardText.fontSize)
    .fillColor(palette.primary.foreground)
    .text("Through Clipboard", throughClipboardText.x, throughClipboardText.y, {
      align: "left",
    });

  // -- Independent Contractor text
  const independentContractorText = {
    content: "Independent Contractor",
    fontSize: mmToPoints(4),
    padding: {
      vertical: mmToPoints(1.5),
      horizontal: mmToPoints(2),
    },
    font: text.fonts.bold,
    background: {
      x: badge.x + badge.padding.left,
      radius: mmToPoints(1.5),
      // Will be calculated
      y: 0,
      width: 0,
      height: 0,
    },
    // Will be calculated
    width: 0,
    height: 0,
  };
  // Measure independent contractor text dimensions
  pdf.font(independentContractorText.font).fontSize(independentContractorText.fontSize);
  const independentContractorTextWidth = pdf.widthOfString(independentContractorText.content);
  independentContractorText.width =
    independentContractorTextWidth + independentContractorText.padding.horizontal * 2;
  independentContractorText.height =
    independentContractorText.fontSize + independentContractorText.padding.vertical * 2;

  // Calculate positions from bottom up
  independentContractorText.background.y =
    badge.y + badge.height - badge.padding.bottom - independentContractorText.height;

  // Add independent contractor background rectangle
  pdf
    .lineWidth(0)
    .roundedRect(
      independentContractorText.background.x,
      independentContractorText.background.y,
      independentContractorText.width,
      independentContractorText.height,
      independentContractorText.background.radius
    )
    .fillAndStroke(palette.primary.foreground, palette.primary.foreground);

  // Add independent contractor text
  pdf
    .font(independentContractorText.font)
    .fontSize(independentContractorText.fontSize)
    .fillColor(palette.primary.background)
    .text(
      independentContractorText.content,
      independentContractorText.background.x + independentContractorText.padding.horizontal,
      independentContractorText.background.y +
        independentContractorText.height / 2 +
        mmToPoints(0.3),
      {
        align: "left",
        baseline: "middle",
      }
    );

  // -- Logo
  const logoBase64 = await convertImageUrlToBase64(logoUrl);
  const logoImageWidth = mmToPoints(17);
  const logoImageHeight = logoImageWidth * (15 / 14);

  const logoImage = {
    x: badge.x + badge.width - badge.padding.right - logoImageWidth,
    y: badge.y + badge.height - badge.padding.bottom - logoImageHeight,
    width: logoImageWidth,
    height: logoImageHeight,
  };

  pdf.image(logoBase64, logoImage.x, logoImage.y, {
    width: logoImage.width,
    height: logoImage.height,
    fit: [logoImage.width, logoImage.height],
  });

  // Collect the chunks of the PDF and return a blob
  const chunks: Uint8Array[] = [];

  pdf.on("data", (chunk: Uint8Array) => {
    chunks.push(chunk);
  });

  const promise = new Promise<{ blob: Blob; pdfKey: string }>((resolve) => {
    pdf.on("end", () => {
      // Convert Uint8Array chunks to proper ArrayBuffer type for TypeScript
      // Create new Uint8Array instances to ensure proper ArrayBuffer type
      const blobParts = chunks.map((chunk) => new Uint8Array(chunk).buffer);
      const blob = new Blob(blobParts, { type: "application/pdf" });
      resolve({ blob, pdfKey: uuid() });
    });
  });

  pdf.end();

  return await promise;
}
/* eslint-enable max-lines */
